import {
  type ClipboardEventHandler,
  forwardRef,
  type KeyboardEvent,
  type ReactNode,
  useId,
} from "react"

import { BaseInput } from "../BaseInput"
import { Icon, type IconName } from "../Icon"
import { FormInputField, type FormInputFieldProps } from "./FormField"

export interface FormInputProps
  extends Pick<
    FormInputFieldProps,
    "disabled" | "error" | "sx" | "size" | "description"
  > {
  type?: "text" | "email" | "password" | "number"
  label?: string
  placeholder?: string
  value: string
  icon?: IconName
  autoComplete?: AutoCompleteValue
  onKeyDown?: (event: KeyboardEvent) => void
  onValueChange?: (value: string) => void
  onPaste?: ClipboardEventHandler<HTMLInputElement>
  onBlur?: () => void
  prefix?: ReactNode
  prefixTooltip?: string
  suffix?: ReactNode
  maxLength?: number
}

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  function FormInput(props, ref) {
    const {
      type = "text",
      label,
      disabled = false,
      error = false,
      onValueChange,
      icon,
      sx,
      value,
      size,
      prefix,
      prefixTooltip,
      suffix,
      autoComplete,
      placeholder,
      onBlur,
      onKeyDown,
      onPaste,
      description,
      maxLength,
    } = props

    const id = useId()

    return (
      <FormInputField
        id={id}
        label={label}
        disabled={disabled}
        sx={sx}
        size={size}
        error={error}
        description={description}
      >
        <BaseInput
          ref={ref}
          startAdornment={
            icon && <Icon name={icon} size="medium" color="grey11" />
          }
          prefix={prefix}
          prefixTooltip={prefixTooltip}
          suffix={suffix}
          id={id}
          disabled={disabled}
          value={value}
          autoComplete={autoComplete}
          placeholder={placeholder}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          padding={{
            adornment: {
              inline: 8,
            },
            input: {
              left: icon ? 0 : 12,
              right: 12,
            },
          }}
          variant={error ? "error" : "normal"}
          type={type}
          onValueChange={(value) => onValueChange?.(value)}
          sx={{
            ...sx,
            alignSelf: "stretch",
          }}
          inputMode={type === "number" ? "decimal" : "text"}
          maxLength={maxLength}
          aria-invalid={error ? "true" : undefined}
          aria-errormessage={error ? `${id}-form-field-error` : undefined}
        />
      </FormInputField>
    )
  },
)

type AutoCompleteValue = "username" | "current-password"
